import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Receipt, Mail, Smartphone, Bell, Save, Send, Plus, X, Clock, Palette, Truck, CalendarDays, Package, Upload, FileSpreadsheet, Database, BarChart3, ShoppingCart, CreditCard, Timer, Users as UsersIcon, Languages, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import Design, { LogoUploader } from "@/pages/Design";
import EmployeeTab from "@/components/settings/EmployeeTab";
import GoogleCalendarTab from "@/components/GoogleCalendarTab";
import ImportsTab from "@/components/ImportsTab";
import BillingExportDialog from "@/components/billing/BillingExportDialog";
import BackupTab from "@/components/settings/BackupTab";
import DailySummaryTab from "@/components/settings/DailySummaryTab";
import ChecklistFieldsTab from "@/components/settings/ChecklistFieldsTab";
import DeviceCatalogTab from "@/components/settings/DeviceCatalogTab";
import PayPalSettingsTab from "@/components/settings/PayPalSettingsTab";
import ReminderSettingsTab from "@/components/settings/ReminderSettingsTab";
import SettingsLanguagesTab from "@/components/settings/SettingsLanguagesTab";
import SettingsAddonsTab from "@/components/settings/SettingsAddonsTab";
import PostRepairRulesPanel from "@/components/settings/PostRepairRulesPanel";
import ReferralSourcesCard from "@/components/settings/ReferralSourcesCard";
import RepairDropdownsCard from "@/components/settings/RepairDropdownsCard";

const TABS = [
  { id: "company", label: "Empresa", icon: Building2 },
  { id: "billing", label: "Facturación", icon: Receipt },
  { id: "inventory", label: "Inventario", icon: Package },
  { id: "devices", label: "Catálogo de dispositivos", icon: Smartphone },
  { id: "catalog", label: "Otros catálogos", icon: Smartphone },
  { id: "notifications", label: "Notificaciones", icon: Bell },
  { id: "messaging", label: "Mensajería", icon: Truck },
  { id: "design", label: "Diseño", icon: Palette },
  { id: "languages", label: "Idiomas", icon: Languages },
  { id: "google_calendar", label: "Google", icon: CalendarDays },
  { id: "time_clock", label: "Control horario", icon: Clock },
  { id: "daily_summary", label: "Resumen diario", icon: BarChart3 },
  { id: "checklist", label: "Checklist", icon: ClipboardCheck },
  { id: "imports", label: "Importar datos", icon: Upload },
  { id: "cross_sell", label: "Venta cruzada", icon: ShoppingCart },
  { id: "addons", label: "Add-ons", icon: Package },
  { id: "paypal", label: "PayPal", icon: CreditCard },
  { id: "reminders", label: "Recordatorios", icon: Timer },
  { id: "post_repair", label: "Emails post-reparación", icon: Mail },
  { id: "employee", label: "Empleado", icon: UsersIcon },
  { id: "backup", label: "Backup", icon: Database },
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin") || user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [tab, setTab] = useState(initialTab && TABS.find((t) => t.id === initialTab) ? initialTab : "company");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  // El sync URL ↔ tab debe ignorar `searchParams`/`setSearchParams` para evitar
  // un loop: nosotros mismos los modificamos dentro del effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab !== searchParams.get("tab")) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
  }, [tab]);

  useEffect(() => {
    api.get("/settings").then((r) => setData(r.data));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/settings", patch);
      setData(r.data);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-10 text-sm text-[#525252]">Cargando...</div>;

  return (
    <div className="p-6 lg:p-10">
      <header className="pb-6 border-b border-dashed border-[#D5D7CE]">
        <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
          <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Sistema
        </div>
        <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight">Configuración</h1>
        {!isAdmin && (
          <p className="text-xs text-amber-800 mt-3 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg inline-block">
            Solo los administradores pueden modificar la configuración.
          </p>
        )}
      </header>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Tabs */}
        <nav className="space-y-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-all ${
                tab === t.id
                  ? "bg-[#0F1210] text-white shadow-md"
                  : "text-[#4B5158] hover:bg-white hover:text-[#0F1210]"
              }`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </nav>

        <div>
          {tab === "company" && <CompanyTab data={data} save={save} disabled={!isAdmin || saving} isAdmin={isAdmin} />}
          {tab === "billing" && <BillingTab data={data} save={save} disabled={!isAdmin || saving} />}
          {tab === "inventory" && <InventorySettingsTab data={data} save={save} disabled={!isAdmin || saving} />}
          {tab === "smtp" && <SmtpTab data={data} save={save} disabled={!isAdmin || saving} />}
          {tab === "devices" && <DeviceCatalogTab data={data} save={save} disabled={!isAdmin || saving} />}
          {tab === "catalog" && <CatalogTab data={data} save={save} disabled={!isAdmin || saving} />}
          {tab === "notifications" && <NotificationsTab data={data} save={save} disabled={!isAdmin || saving} />}
          {tab === "messaging" && <MessagingTab data={data} save={save} disabled={!isAdmin || saving} />}
          {tab === "design" && <Design embedded />}
          {tab === "google_calendar" && <GoogleCalendarTab disabled={!isAdmin} />}
          {tab === "time_clock" && <TimeClockTab data={data} save={save} disabled={!isAdmin || saving} isAdmin={isAdmin} />}
          {tab === "daily_summary" && <DailySummaryTab />}
          {tab === "checklist" && <ChecklistFieldsTab />}
          {tab === "imports" && <ImportsTab />}
          {tab === "addons" && <SettingsAddonsTab />}
          {tab === "languages" && <SettingsLanguagesTab />}
          {tab === "cross_sell" && (
            <div className="border border-[#111110] p-6 text-center" data-testid="cross-sell-tab-redirect">
              <ShoppingCart size={32} className="mx-auto mb-3 text-[#6E8B3D]" />
              <h2 className="font-display text-xl font-black mb-1">Venta Cruzada</h2>
              <p className="text-sm text-[#525252] mb-4">
                Gestiona las reglas de venta cruzada por Equipo y Modelo.
              </p>
              <Link
                to="/settings/cross-sell"
                data-testid="cross-sell-link"
                className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold"
              >
                Abrir gestor de reglas
              </Link>
            </div>
          )}
          {tab === "paypal" && <PayPalSettingsTab />}
          {tab === "reminders" && <ReminderSettingsTab />}
          {tab === "post_repair" && <PostRepairRulesPanel />}
          {tab === "employee" && <EmployeeTab />}
          {tab === "backup" && <BackupTab />}
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="ds-card">
      <div className="px-4 py-3 border-b border-[#E5E5E0] font-display font-bold text-sm">
        {title}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, disabled, testid, hint }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none disabled:bg-[#F7F7F4]"
      />
      {hint && <div className="text-[10px] text-[#525252] mt-1">{hint}</div>}
    </div>
  );
}

function SaveBtn({ onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
    >
      <Save size={14} /> Guardar
    </button>
  );
}

function CompanyTab({ data, save, disabled, isAdmin }) {
  const [c, setC] = useState(data.company);
  useEffect(() => setC(data.company), [data.company]);
  const senderPreviewName = (c.sender_name || c.name || "").trim();
  const senderPreviewEmail = (c.email || "").trim();
  return (
    <div className="space-y-4">
    <Card title="Datos de empresa">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Nombre comercial" value={c.name} onChange={(v) => setC({ ...c, name: v })} disabled={disabled} testid="company-name" />
        <Field label="Razón social" value={c.legal_name} onChange={(v) => setC({ ...c, legal_name: v })} disabled={disabled} />
        <Field label="NIF / CIF" value={c.tax_id} onChange={(v) => setC({ ...c, tax_id: v })} disabled={disabled} testid="company-taxid" />
        <Field label="Teléfono" value={c.phone} onChange={(v) => setC({ ...c, phone: v })} disabled={disabled} />
        <Field label="Email" type="email" value={c.email} onChange={(v) => setC({ ...c, email: v })} disabled={disabled} />
        <Field label="Web" value={c.website} onChange={(v) => setC({ ...c, website: v })} disabled={disabled} />
        <div className="md:col-span-2">
          <Field label="Dirección" value={c.address} onChange={(v) => setC({ ...c, address: v })} disabled={disabled} />
        </div>
        <Field label="Código postal" value={c.postal_code || ""} onChange={(v) => setC({ ...c, postal_code: v })} disabled={disabled} testid="company-postal-code" />
        <Field label="Población" value={c.city || ""} onChange={(v) => setC({ ...c, city: v })} disabled={disabled} testid="company-city" />
        <Field label="Provincia" value={c.province || ""} onChange={(v) => setC({ ...c, province: v })} disabled={disabled} testid="company-province" />
        <Field label="País" value={c.country || ""} onChange={(v) => setC({ ...c, country: v })} disabled={disabled} testid="company-country" />
        <div className="md:col-span-2">
          <Field
            label="Nombre del remitente (emails)"
            value={c.sender_name || ""}
            onChange={(v) => setC({ ...c, sender_name: v })}
            disabled={disabled}
            placeholder="Ej.: iRepairs · Servicio Técnico iRepairs · Manzana Repair S.L."
            testid="company-sender-name"
          />
          <p className="text-[10px] text-[#525252] mt-1 italic">
            Se muestra como nombre visible del remitente en facturas, albaranes, avisos de recogida,
            presupuestos y cualquier email enviado desde la aplicación (vía Gmail API).
            Si se deja vacío se usa el &quot;Nombre comercial&quot;.
          </p>
          <div className="mt-2 border border-[#E5E5E5] bg-[#FAFAFA] p-3" data-testid="sender-preview">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">
              Vista previa del remitente
            </div>
            <div className="text-xs text-[#404040] space-y-0.5">
              <div>Nombre mostrado: <strong>{senderPreviewName || <em className="text-[#A3A3A3]">(sin nombre — solo email)</em>}</strong></div>
              <div>Correo: <strong>{senderPreviewEmail || <em className="text-[#A3A3A3]">(configura el email de empresa)</em>}</strong></div>
              <div className="pt-1">
                Resultado: <code className="font-mono bg-white border border-[#E5E5E5] px-1 py-0.5">{senderPreviewName && senderPreviewEmail ? `${senderPreviewName} <${senderPreviewEmail}>` : (senderPreviewEmail || "—")}</code>
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1 block">
            Logo de la empresa
          </label>
          <LogoUploader
            value={c.logo_data_b64 || ""}
            onChange={(newUri) => setC({ ...c, logo_data_b64: newUri, logo_url: "" })}
          />
          <p className="text-[10px] text-[#525252] mt-1 italic">
            Se usa tanto en los PDFs (esquina superior derecha) como en el login y en el menú lateral de la aplicación.
          </p>
        </div>
      </div>
      <div className="mt-4">
        <SaveBtn onClick={() => save({ company: c })} disabled={disabled} testid="company-save" />
      </div>
    </Card>

    {/* Iter 160 — Opciones de "¿Cómo nos has conocido?" */}
    <ReferralSourcesCard isAdmin={isAdmin} />

    {/* Iter 161 — Desplegables de la ficha de reparación */}
    <RepairDropdownsCard data={data} save={save} disabled={disabled} />

    {/* Iter 104 — Términos y Condiciones PDF adjunto a documentos */}
    <TermsPdfCard disabled={disabled} />
    </div>
  );
}

function TermsPdfCard({ disabled }) {
  const [info, setInfo] = useState({ has_file: false, filename: "", size: 0, pages: 0, uploaded_at: "" });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/settings/terms-pdf");
      setInfo(r.data);
    } catch {
      /* silencioso */
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo se admiten archivos PDF (.pdf).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/terms-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Términos y condiciones subidos");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const del = async () => {
    if (!window.confirm("¿Eliminar el PDF de Términos y Condiciones? Los documentos nuevos no lo adjuntarán.")) return;
    try {
      await api.delete("/settings/terms-pdf");
      toast.success("PDF eliminado");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openPreview = async () => {
    try {
      const r = await fetch(`${api.defaults.baseURL}/settings/terms-pdf/download`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
      });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error("No se pudo abrir la vista previa");
    }
  };

  const sizeKb = info.size ? Math.round(info.size / 1024) : 0;

  return (
    <Card title="Términos y condiciones (PDF)">
      <p className="text-xs text-[#525252] mb-3">
        Sube un único archivo PDF con tus términos y condiciones. Se adjuntará
        automáticamente como página(s) final(es) de todos los documentos
        generados: <strong>Presupuestos, Facturas, Albaranes y Abonos</strong>.
        Mantiene la calidad original del PDF y no se pierde en futuros despliegues.
      </p>

      {info.has_file ? (
        <div className="border border-[#E5E5E0] p-3 bg-[#FAFAFA] space-y-2" data-testid="terms-pdf-current">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="terms-pdf-filename">{info.filename || "terms.pdf"}</div>
              <div className="text-[11px] text-[#525252] font-mono">
                {info.pages} {info.pages === 1 ? "página" : "páginas"} · {sizeKb} KB
                {info.uploaded_at && <> · subido {info.uploaded_at.slice(0, 10)}</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openPreview}
                className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-1.5 text-xs"
                data-testid="terms-pdf-preview"
              >
                Ver PDF
              </button>
              <label className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-1.5 text-xs cursor-pointer">
                Reemplazar
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={upload} disabled={uploading || disabled} data-testid="terms-pdf-replace" />
              </label>
              <button
                type="button"
                onClick={del}
                disabled={disabled || uploading}
                className="border border-red-600 text-red-700 hover:bg-red-600 hover:text-white px-3 py-1.5 text-xs disabled:opacity-40"
                data-testid="terms-pdf-delete"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-[#B5B5B5] p-4 bg-[#FAFAFA]" data-testid="terms-pdf-empty">
          <p className="text-xs text-[#525252] mb-2">No hay ningún PDF de Términos y Condiciones subido todavía.</p>
          <label className="inline-block bg-[#111110] hover:bg-[#6E8B3D] text-white px-3 py-2 text-xs cursor-pointer">
            {uploading ? "Subiendo..." : "Subir PDF"}
            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={upload} disabled={uploading || disabled} data-testid="terms-pdf-upload" />
          </label>
        </div>
      )}
    </Card>
  );
}

function BillingTab({ data, save, disabled }) {
  const [b, setB] = useState(data.billing);
  const [showExport, setShowExport] = useState(false);
  useEffect(() => setB(data.billing), [data.billing]);
  return (
    <div className="space-y-4">
      <Card title="Facturación e impuestos">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Moneda" value={b.currency} onChange={(v) => setB({ ...b, currency: v })} disabled={disabled} placeholder="EUR" />
        <Field label="IVA por defecto (%)" type="number" value={b.default_vat} onChange={(v) => setB({ ...b, default_vat: Number(v) })} disabled={disabled} testid="billing-vat" />
        <Field label="Prefijo de factura" value={b.invoice_prefix} onChange={(v) => setB({ ...b, invoice_prefix: v })} disabled={disabled} placeholder="F-2026-" />
        <Field label="Próximo nº de factura" type="number" value={b.next_invoice_number} onChange={(v) => setB({ ...b, next_invoice_number: Number(v) })} disabled={disabled} />
        <Field label="Próximo nº de reparación (REP-)" type="number" value={b.next_repair_number} onChange={(v) => setB({ ...b, next_repair_number: Number(v) })} disabled={disabled} hint="Aplica a partir de la siguiente reparación creada" />
        <Field label="Próximo nº de cotización (COT-)" type="number" value={b.next_quote_number} onChange={(v) => setB({ ...b, next_quote_number: Number(v) })} disabled={disabled} />
        <WarrantyDefaultField b={b} setB={setB} disabled={disabled} />
        <div className="flex items-center gap-2 md:col-span-2 pt-2">
          <input
            id="prices_include_vat"
            type="checkbox"
            checked={!!b.prices_include_vat}
            disabled={disabled}
            onChange={(e) => setB({ ...b, prices_include_vat: e.target.checked })}
            data-testid="billing-prices-include-vat"
            className="h-4 w-4 accent-[#6E8B3D]"
          />
          <label htmlFor="prices_include_vat" className="text-sm text-[#111110]">
            Mostrar precio unitario con IVA incluido en los PDF (Factura, Albarán, Abono)
          </label>
        </div>
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            Términos al pie de cotización/factura
          </label>
          <textarea
            value={b.footer_terms || ""}
            disabled={disabled}
            onChange={(e) => setB({ ...b, footer_terms: e.target.value })}
            rows={3}
            className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          />
        </div>
      </div>
      <div className="mt-4">
        <SaveBtn onClick={() => save({ billing: b })} disabled={disabled} testid="billing-save" />
      </div>
    </Card>

      <Card title="Exportar facturación">
        <div className="text-xs text-[#525252] mb-3">
          Genera un listado en pantalla y un Excel con todas las facturas y albaranes según el periodo y filtros que elijas.
        </div>
        <button
          onClick={() => setShowExport(true)}
          className="bg-[#111110] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#2a2a28]"
          data-testid="billing-open-export"
        >
          <FileSpreadsheet size={14} />
          Exportar facturación
        </button>
      </Card>

      {showExport && <BillingExportDialog onClose={() => setShowExport(false)} />}
    </div>
  );
}

function SmtpTab({ data, save, disabled }) {
  const [s, setS] = useState(data.smtp);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => setS(data.smtp), [data.smtp]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/smtp/test", { to: testEmail });
      toast.success(r.data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card title="Servidor SMTP">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Host *" value={s.host} onChange={(v) => setS({ ...s, host: v })} disabled={disabled} placeholder="smtp.tudominio.com" testid="smtp-host" />
        <Field label="Puerto *" type="number" value={s.port} onChange={(v) => setS({ ...s, port: Number(v) })} disabled={disabled} placeholder="587" testid="smtp-port" />
        <Field label="Usuario" value={s.username} onChange={(v) => setS({ ...s, username: v })} disabled={disabled} />
        <Field label="Contraseña" type="password" value={s.password} onChange={(v) => setS({ ...s, password: v })} disabled={disabled} placeholder="(sin cambios si vacío al guardar)" />
        <Field label="Email remitente" type="email" value={s.from_email} onChange={(v) => setS({ ...s, from_email: v })} disabled={disabled} testid="smtp-from" />
        <Field label="Nombre remitente" value={s.from_name} onChange={(v) => setS({ ...s, from_name: v })} disabled={disabled} placeholder="Mi Taller" />
        <div className="md:col-span-2 flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!s.use_tls}
            disabled={disabled}
            onChange={(e) => setS({ ...s, use_tls: e.target.checked })}
            id="usetls"
          />
          <label htmlFor="usetls" className="text-sm">Usar STARTTLS (recomendado para puerto 587)</label>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <SaveBtn onClick={() => save({ smtp: s })} disabled={disabled} testid="smtp-save" />
      </div>

      <div className="mt-6 pt-4 border-t border-[#E5E5E0]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Prueba de envío
        </div>
        <div className="mt-2 flex items-center gap-2">
          <input
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            type="email"
            placeholder="tu@email.com"
            data-testid="smtp-test-email"
            className="flex-1 ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          />
          <button
            onClick={sendTest}
            disabled={!testEmail || testing}
            data-testid="smtp-test-send"
            className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm disabled:opacity-50"
          >
            <Send size={14} /> {testing ? "Enviando..." : "Enviar prueba"}
          </button>
        </div>
      </div>
    </Card>
  );
}

function CatalogTab({ data, save, disabled }) {
  const [pwdList, setPwdList] = useState(data.password_status_catalog || []);
  const [newPwd, setNewPwd] = useState("");
  const [catList, setCatList] = useState(data.expense_categories_catalog || []);
  const [newCat, setNewCat] = useState("");
  useEffect(() => {
    setPwdList(data.password_status_catalog || []);
    setCatList(data.expense_categories_catalog || []);
  }, [data.password_status_catalog, data.expense_categories_catalog]);

  const addPwd = () => {
    if (!newPwd.trim()) return;
    setPwdList([...pwdList, newPwd.trim()]);
    setNewPwd("");
  };
  const removePwd = (i) => setPwdList(pwdList.filter((_, idx) => idx !== i));
  const addCat = () => {
    if (!newCat.trim()) return;
    setCatList([...catList, newCat.trim()]);
    setNewCat("");
  };
  const removeCat = (i) => setCatList(catList.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6">
    <Card title="Estado de la contraseña del equipo">
      <p className="text-xs text-[#525252] mb-3">
        Estados posibles para el campo &laquo;Estado de la contraseña&raquo; en la ficha de reparación.
        Si seleccionas <strong>&laquo;Cliente deja contraseña&raquo;</strong>, el campo de contraseña pasa a ser obligatorio.
      </p>
      <div className="flex flex-wrap gap-2">
        {pwdList.map((d, i) => (
          <span key={d} className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-[#111110] text-sm">
            {d}
            {!disabled && (
              <button onClick={() => removePwd(i)} className="hover:text-red-600">
                <X size={12} />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="mt-4 flex items-center gap-2">
          <input
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPwd())}
            placeholder="Añadir estado (ej. PIN 4 dígitos)"
            data-testid="pwd-status-input"
            className="flex-1 max-w-xs ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          />
          <button onClick={addPwd} className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm inline-flex items-center gap-1">
            <Plus size={14} /> Añadir
          </button>
        </div>
      )}
      <div className="mt-4">
        <SaveBtn onClick={() => save({ password_status_catalog: pwdList })} disabled={disabled} testid="pwd-status-save" />
      </div>
    </Card>

    <Card title="Categorías de gasto">
      <p className="text-xs text-[#525252] mb-3">
        Categorías disponibles al crear un gasto en <strong>Facturación → Gastos</strong>.
      </p>
      <div className="flex flex-wrap gap-2">
        {catList.map((d, i) => (
          <span key={d} className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-[#111110] text-sm">
            {d}
            {!disabled && (
              <button onClick={() => removeCat(i)} className="hover:text-red-600">
                <X size={12} />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="mt-4 flex items-center gap-2">
          <input
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCat())}
            placeholder="Añadir categoría (ej. Suministros)"
            data-testid="expense-cat-input"
            className="flex-1 max-w-xs ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          />
          <button onClick={addCat} className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm inline-flex items-center gap-1">
            <Plus size={14} /> Añadir
          </button>
        </div>
      )}
      <div className="mt-4">
        <SaveBtn onClick={() => save({ expense_categories_catalog: catList })} disabled={disabled} testid="expense-cat-save" />
      </div>
    </Card>

    {/* Iter 99 — Corrección: gestión de "Estados y secciones" */}
    <SectionsByStatusCard data={data} save={save} disabled={disabled} />
    </div>
  );
}

function SectionsByStatusCard({ data, save, disabled }) {
  const [catalog, setCatalog] = useState(data.section_catalog || []);
  const [openStatus, setOpenStatus] = useState({});
  const [newSection, setNewSection] = useState({});

  useEffect(() => {
    setCatalog(data.section_catalog || []);
  }, [data.section_catalog]);

  // Agrupa por estado. Incluye una clave "" para secciones globales.
  const grouped = useMemo(() => {
    const map = {};
    for (const s of STATUS_ORDER) map[s] = [];
    map[""] = [];
    (catalog || []).forEach((sec, idx) => {
      const st = sec?.status || "";
      if (!map[st]) map[st] = [];
      map[st].push({ ...sec, __idx: idx });
    });
    return map;
  }, [catalog]);

  const addSection = (status) => {
    const name = (newSection[status] || "").trim();
    if (!name) return;
    const dup = (catalog || []).some(
      (s) => s?.name?.toLowerCase() === name.toLowerCase() && (s?.status || "") === status,
    );
    if (dup) {
      toast.info("Ya existe una sección con ese nombre en este estado");
      return;
    }
    setCatalog([...(catalog || []), { name, status, active: true }]);
    setNewSection({ ...newSection, [status]: "" });
  };

  const toggleActive = (idx) => {
    const next = [...catalog];
    next[idx] = { ...next[idx], active: next[idx].active === false };
    setCatalog(next);
  };

  const removeSection = (idx) => {
    const sec = catalog[idx];
    if (!window.confirm(`¿Quitar "${sec.name}" del catálogo?`)) return;
    setCatalog(catalog.filter((_, i) => i !== idx));
  };

  const renameSection = (idx) => {
    const sec = catalog[idx];
    const newName = window.prompt("Nuevo nombre de la sección:", sec.name);
    if (!newName || !newName.trim() || newName.trim() === sec.name) return;
    const dup = catalog.some(
      (s, i) => i !== idx && s?.name?.toLowerCase() === newName.trim().toLowerCase() && (s?.status || "") === (sec.status || ""),
    );
    if (dup) {
      toast.info("Ya existe una sección con ese nombre en este estado");
      return;
    }
    const next = [...catalog];
    next[idx] = { ...next[idx], name: newName.trim() };
    setCatalog(next);
  };

  const moveSection = (idx, dir) => {
    const sec = catalog[idx];
    const sameStatus = catalog
      .map((s, i) => ((s?.status || "") === (sec.status || "") ? i : -1))
      .filter((i) => i >= 0);
    const posInGroup = sameStatus.indexOf(idx);
    const swapAt = dir === "up" ? sameStatus[posInGroup - 1] : sameStatus[posInGroup + 1];
    if (swapAt === undefined) return;
    const next = [...catalog];
    [next[idx], next[swapAt]] = [next[swapAt], next[idx]];
    setCatalog(next);
  };

  const statusesToRender = [...STATUS_ORDER, ""];

  return (
    <Card title="Estados y secciones">
      <p className="text-xs text-[#525252] mb-3">
        Vincula manualmente las secciones a cada estado. En la ficha de reparación, el
        desplegable <strong>Sección</strong> mostrará únicamente las secciones asignadas al
        <strong> estado actual</strong> de la reparación (más las secciones globales).
        <br />
        Las reparaciones existentes conservan su sección tal cual — este cambio solo aplica
        al crear una nueva reparación o al cambiar el estado manualmente.
      </p>
      <div className="space-y-2" data-testid="sections-by-status-list">
        {statusesToRender.map((status) => {
          const key = status || "__global";
          const label = status === "" ? "Secciones globales (visibles en todos los estados)" : (STATUS_LABELS[status] || status);
          const items = grouped[status] || [];
          const isOpen = openStatus[status] ?? false;
          return (
            <div key={key} className="border border-[#E5E5E0]">
              <button
                type="button"
                onClick={() => setOpenStatus({ ...openStatus, [status]: !isOpen })}
                className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#F7F7F4]"
                data-testid={`sections-status-toggle-${key}`}
              >
                <span className="font-mono uppercase text-xs">{label}</span>
                <span className="text-[10px] text-[#525252]">
                  {items.filter((s) => s.active !== false).length} activa(s) · {items.length} total
                  <span className="ml-2">{isOpen ? "▼" : "▶"}</span>
                </span>
              </button>
              {isOpen && (
                <div className="p-3 space-y-2 border-t border-[#E5E5E0] bg-[#FAFAFA]">
                  {items.length === 0 && (
                    <p className="text-xs text-[#525252]">Sin secciones vinculadas todavía.</p>
                  )}
                  {items.map((sec) => (
                    <div
                      key={sec.__idx}
                      className={`flex items-center gap-2 px-2 py-1.5 bg-white border ${sec.active === false ? "border-dashed border-[#B5B5B5] opacity-60" : "border-[#111110]"}`}
                      data-testid={`sections-item-${sec.__idx}`}
                    >
                      <span className={`text-sm flex-1 ${sec.active === false ? "line-through text-[#525252]" : ""}`}>
                        {sec.name}
                      </span>
                      {!disabled && (
                        <>
                          <button
                            type="button"
                            onClick={() => moveSection(sec.__idx, "up")}
                            className="text-xs hover:text-[#6E8B3D] px-1"
                            title="Subir"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSection(sec.__idx, "down")}
                            className="text-xs hover:text-[#6E8B3D] px-1"
                            title="Bajar"
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            onClick={() => renameSection(sec.__idx)}
                            className="text-[10px] font-mono uppercase tracking-wider hover:text-[#6E8B3D] px-1"
                            title="Renombrar"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(sec.__idx)}
                            className={`text-[10px] font-mono uppercase tracking-wider hover:underline px-1 ${sec.active === false ? "text-emerald-700" : "text-amber-700"}`}
                          >
                            {sec.active === false ? "Activar" : "Desactivar"}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSection(sec.__idx)}
                            className="hover:text-red-600"
                            title="Quitar"
                            aria-label={`Quitar ${sec.name}`}
                          >
                            <X size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {!disabled && (
                    <div className="flex items-center gap-2 pt-2 border-t border-dashed border-[#E5E5E0]">
                      <input
                        value={newSection[status] || ""}
                        onChange={(e) => setNewSection({ ...newSection, [status]: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSection(status))}
                        placeholder={`Añadir sección · ${label}`}
                        data-testid={`sections-add-input-${key}`}
                        className="flex-1 ds-card focus:border-[#6E8B3D] outline-none px-2 py-1.5 text-xs rounded-none"
                      />
                      <button
                        type="button"
                        onClick={() => addSection(status)}
                        className="bg-[#111110] hover:bg-[#6E8B3D] text-white px-3 py-1.5 text-xs inline-flex items-center gap-1"
                        data-testid={`sections-add-btn-${key}`}
                      >
                        <Plus size={11} /> Añadir
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <SaveBtn
          onClick={() => save({ section_catalog: catalog })}
          disabled={disabled}
          testid="sections-by-status-save"
        />
      </div>
    </Card>
  );
}

function NotificationsTab({ data, save, disabled }) {
  const [n, setN] = useState(data.notifications);
  useEffect(() => setN(data.notifications), [data.notifications]);
  const items = [
    { key: "on_order_created", label: "Al crear una orden de reparación" },
    { key: "on_ready_for_pickup", label: "Cuando una reparación cambia a 'Listo para entregar'" },
    { key: "on_quote_approved", label: "Cuando se aprueba una cotización" },
  ];
  return (
    <Card title="Notificaciones automáticas por email">
      <p className="text-xs text-[#525252] mb-3">
        Envía emails al cliente automáticamente. Requiere SMTP configurado y email del cliente.
      </p>
      <div className="space-y-2">
        {items.map((it) => (
          <label key={it.key} className="flex items-center gap-3 p-2 border border-[#E5E5E0] hover:border-[#111110]">
            <input
              type="checkbox"
              checked={!!n[it.key]}
              disabled={disabled}
              onChange={(e) => setN({ ...n, [it.key]: e.target.checked })}
              data-testid={`notif-${it.key}`}
            />
            <span className="text-sm">{it.label}</span>
          </label>
        ))}
      </div>
      <div className="mt-4">
        <SaveBtn onClick={() => save({ notifications: n })} disabled={disabled} testid="notif-save" />
      </div>
    </Card>
  );
}

function MessagingTab({ data, save, disabled }) {
  const initial = data.messaging || { pickup: {}, send: {} };
  const [pickup, setPickup] = useState({
    recipient: initial.pickup?.recipient || "",
    subject: initial.pickup?.subject || "",
    body: initial.pickup?.body || "",
  });
  const [sendBlock, setSendBlock] = useState({
    recipient: initial.send?.recipient || "",
    subject: initial.send?.subject || "",
    body: initial.send?.body || "",
  });
  useEffect(() => {
    const m = data.messaging || { pickup: {}, send: {} };
    setPickup({ recipient: m.pickup?.recipient || "", subject: m.pickup?.subject || "", body: m.pickup?.body || "" });
    setSendBlock({ recipient: m.send?.recipient || "", subject: m.send?.subject || "", body: m.send?.body || "" });
  }, [data.messaging]);

  const VARS = [
    "{cliente_nombre}", "{cliente_direccion}", "{cliente_telefono}",
    "{cliente_cp}", "{cliente_poblacion}", "{tipo_accion}",
    "{indicaciones}", "{usuario_actual}", "{fecha_actual}", "{hora_actual}",
  ];

  const saveAll = () => save({ messaging: { pickup, send: sendBlock } });

  return (
    <div className="space-y-6">
      <Card title="Mensaje para Recoger">
        <p className="text-xs text-[#525252] mb-3">
          Plantilla del email que se enviará cuando un usuario pulse <strong>Recogida / Envío → Recoger</strong> en la ficha de un cliente.
        </p>
        <MessageBlock value={pickup} onChange={setPickup} disabled={disabled} testidPrefix="msg-pickup" />
      </Card>

      <Card title="Mensaje para Enviar">
        <p className="text-xs text-[#525252] mb-3">
          Plantilla del email que se enviará cuando un usuario pulse <strong>Recogida / Envío → Enviar</strong>.
        </p>
        <MessageBlock value={sendBlock} onChange={setSendBlock} disabled={disabled} testidPrefix="msg-send" />
      </Card>

      <Card title="Variables disponibles">
        <p className="text-xs text-[#525252] mb-2">
          Inserta cualquiera de estas variables en el asunto o cuerpo del mensaje. Se sustituirán por los datos reales al enviar.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {VARS.map((v) => (
            <code key={v} className="px-1.5 py-0.5 bg-[#F0F4E8] border border-[#6E8B3D] text-[10px] font-mono">
              {v}
            </code>
          ))}
        </div>
      </Card>

      <div>
        <SaveBtn onClick={saveAll} disabled={disabled} testid="msg-save" />
      </div>
    </div>
  );
}

function MessageBlock({ value, onChange, disabled, testidPrefix }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Destinatario(s)
        </label>
        <input
          type="text"
          value={value.recipient}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, recipient: e.target.value })}
          placeholder="email@destino.com — separa varios con coma"
          data-testid={`${testidPrefix}-recipient`}
          className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono disabled:bg-[#F7F7F4]"
        />
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Asunto / título
        </label>
        <input
          type="text"
          value={value.subject}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, subject: e.target.value })}
          data-testid={`${testidPrefix}-subject`}
          className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none disabled:bg-[#F7F7F4]"
        />
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Texto del mensaje
        </label>
        <textarea
          value={value.body}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, body: e.target.value })}
          rows={8}
          data-testid={`${testidPrefix}-body`}
          className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono disabled:bg-[#F7F7F4]"
        />
      </div>
    </div>
  );
}

function TemplatesTab({ data, save, disabled }) {
  const [t, setT] = useState(data.email_templates);
  useEffect(() => setT(data.email_templates), [data]);
  const events = [
    { key: "order_created", label: "Orden creada" },
    { key: "ready_for_pickup", label: "Listo para entregar" },
    { key: "quote_approved", label: "Cotización aprobada" },
    { key: "repair_products", label: "PDF Productos / servicios reparación", hasPdf: true },
  ];
  const updateTpl = (k, field, val) => setT({ ...t, [k]: { ...t[k], [field]: val } });
  return (
    <Card title="Plantillas de email">
      <p className="text-xs text-[#525252] mb-3">
        Variables disponibles: <code className="font-mono">{"{{customer_name}}, {{code}}, {{device}}, {{company}}, {{total}}"}</code>
      </p>
      <div className="space-y-5">
        {events.map((e) => (
          <div key={e.key} className="border border-[#E5E5E0] p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D] mb-2">
              {e.label}
            </div>
            <input
              value={t[e.key]?.subject || ""}
              disabled={disabled}
              onChange={(ev) => updateTpl(e.key, "subject", ev.target.value)}
              placeholder="Asunto del email"
              className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
            />
            <textarea
              value={t[e.key]?.body || ""}
              disabled={disabled}
              onChange={(ev) => updateTpl(e.key, "body", ev.target.value)}
              rows={5}
              placeholder="Cuerpo del email"
              className="mt-2 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono"
            />
            {e.hasPdf && (
              <div className="mt-3 pt-3 border-t border-[#E5E5E0] space-y-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  PDF generado
                </div>
                <input
                  value={t[e.key]?.pdf_header || ""}
                  disabled={disabled}
                  onChange={(ev) => updateTpl(e.key, "pdf_header", ev.target.value)}
                  placeholder="Encabezado del PDF"
                  className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
                />
                <input
                  value={t[e.key]?.pdf_footer || ""}
                  disabled={disabled}
                  onChange={(ev) => updateTpl(e.key, "pdf_footer", ev.target.value)}
                  placeholder="Pie del PDF"
                  className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <SaveBtn onClick={() => save({ email_templates: t })} disabled={disabled} testid="templates-save" />
      </div>
    </Card>
  );
}

function TimeClockTab({ data, save, disabled, isAdmin }) {
  const [mode, setMode] = useState(data.time_clock_login_mode || "ask");
  useEffect(() => setMode(data.time_clock_login_mode || "ask"), [data]);
  const opts = [
    { v: "off", t: "Desactivado", d: "No se muestra ningún aviso al iniciar sesión. El usuario debe ir manualmente a Control horario." },
    { v: "ask", t: "Preguntar al iniciar sesión", d: "Después del login, si el usuario no ha fichado entrada hoy, se muestra un aviso para confirmar." },
    { v: "auto", t: "Fichaje automático al login", d: "Después del login, si no hay entrada hoy, se ficha automáticamente con la hora del servidor." },
  ];
  return (
    <div className="space-y-6">
      <Card title="Modo de fichaje al login">
      <p className="text-sm text-[#525252] mb-3">
        Define el comportamiento del sistema cuando un usuario inicia sesión y todavía no ha fichado entrada en el día.
      </p>
      <div className="space-y-2">
        {opts.map((o) => (
          <label key={o.v} className={`flex items-start gap-3 border p-3 cursor-pointer ${mode === o.v ? "border-[#6E8B3D] bg-[#F0F4E8]" : "border-[#E5E5E0]"}`}>
            <input
              type="radio"
              checked={mode === o.v}
              onChange={() => setMode(o.v)}
              disabled={disabled}
              data-testid={`tc-mode-${o.v}`}
              className="mt-1 accent-[#6E8B3D]"
            />
            <div>
              <div className="font-semibold text-sm">{o.t}</div>
              <div className="text-xs text-[#525252]">{o.d}</div>
            </div>
          </label>
        ))}
      </div>
      <div className="mt-4">
        <SaveBtn onClick={() => save({ time_clock_login_mode: mode })} disabled={disabled} testid="tc-mode-save" />
      </div>
    </Card>
      {isAdmin && <TimeClockExportCard />}
    </div>
  );
}

function TimeClockExportCard() {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("__all__");
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(fmt(firstOfMonth));
  const [dateTo, setDateTo] = useState(fmt(today));
  const [busy, setBusy] = useState(false);
  const [auditItems, setAuditItems] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => {
      const list = (r.data || []).filter((u) => u.active !== false);
      setUsers(list);
    }).catch(() => setUsers([]));
  }, []);

  const download = async (format) => {
    if (!dateFrom || !dateTo) {
      toast.error("Selecciona un rango de fechas.");
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({ format, date_from: dateFrom, date_to: dateTo });
      if (userId === "__all__") {
        params.set("all_users", "true");
      } else {
        params.set("user_id", userId);
      }
      const url = `/time-clock/export-range?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], {
        type: format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `control-horario_${dateFrom}_${dateTo}.${format}`;
      link.click();
      toast.success(`Exportación ${format.toUpperCase()} descargada correctamente.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally {
      setBusy(false);
    }
  };

  const loadAudit = async () => {
    try {
      const r = await api.get("/time-clock/export-audit?limit=50");
      setAuditItems(r.data?.items || []);
      setShowAudit(true);
    } catch {
      toast.error("No se pudo cargar el historial de exportaciones");
    }
  };

  return (
    <Card title="Exportación de registros">
      <p className="text-sm text-[#525252] mb-4">
        Consulta y exporta el registro horario de cualquier empleado o de todos, cumpliendo con la normativa de conservación de 4 años.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="md:col-span-2">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
            Empleado
          </label>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="w-full border border-[#E5E5E0] bg-white px-3 py-2 text-sm focus:border-[#6E8B3D] outline-none"
            data-testid="tc-export-user-select"
          >
            <option value="__all__">Todos los empleados</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
            Fecha desde
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full border border-[#E5E5E0] bg-white px-3 py-2 text-sm focus:border-[#6E8B3D] outline-none"
            data-testid="tc-export-date-from"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
            Fecha hasta
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full border border-[#E5E5E0] bg-white px-3 py-2 text-sm focus:border-[#6E8B3D] outline-none"
            data-testid="tc-export-date-to"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => download("pdf")}
          disabled={busy}
          className="px-4 py-2 bg-[#111110] text-white text-sm hover:bg-[#333] disabled:opacity-50 flex items-center gap-2"
          data-testid="tc-export-pdf"
        >
          {busy ? "Generando..." : "Descargar PDF"}
        </button>
        <button
          onClick={() => download("xlsx")}
          disabled={busy}
          className="px-4 py-2 bg-[#6E8B3D] text-white text-sm hover:bg-[#5d7634] disabled:opacity-50 flex items-center gap-2"
          data-testid="tc-export-xlsx"
        >
          {busy ? "Generando..." : "Descargar Excel (.xlsx)"}
        </button>
        <button
          onClick={loadAudit}
          className="px-4 py-2 border border-[#E5E5E0] text-[#111110] text-sm hover:bg-[#F7F7F4] flex items-center gap-2"
          data-testid="tc-export-audit-open"
        >
          Ver historial de exportaciones
        </button>
      </div>

      {showAudit && (
        <div className="mt-6 border border-[#E5E5E0] bg-[#F7F7F4]" data-testid="tc-export-audit-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E0]">
            <div className="font-mono text-[11px] uppercase tracking-widest">Historial de exportaciones</div>
            <button
              onClick={() => setShowAudit(false)}
              className="text-xs text-[#525252] hover:text-[#111110]"
            >
              Cerrar
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {auditItems.length === 0 ? (
              <div className="p-4 text-xs text-[#525252] text-center">Sin exportaciones registradas.</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#525252]">
                    <th className="px-3 py-2 font-mono uppercase tracking-widest">Fecha</th>
                    <th className="px-3 py-2 font-mono uppercase tracking-widest">Exportado por</th>
                    <th className="px-3 py-2 font-mono uppercase tracking-widest">Empleado consultado</th>
                    <th className="px-3 py-2 font-mono uppercase tracking-widest">Periodo</th>
                    <th className="px-3 py-2 font-mono uppercase tracking-widest">Formato</th>
                  </tr>
                </thead>
                <tbody>
                  {auditItems.map((it) => (
                    <tr key={it.id} className="border-t border-[#E5E5E0]">
                      <td className="px-3 py-2">{new Date(it.exported_at).toLocaleString("es-ES")}</td>
                      <td className="px-3 py-2">{it.exported_by_name}</td>
                      <td className="px-3 py-2">{it.target_user_name}</td>
                      <td className="px-3 py-2">{it.date_from} → {it.date_to}</td>
                      <td className="px-3 py-2 uppercase font-mono">{it.format}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      <p className="text-xs text-[#525252] mt-4 italic">
        Cumplimiento normativo: los registros horarios se conservan un mínimo de 4 años. Cada exportación queda auditada (quién, cuándo, qué empleado y qué periodo).
      </p>
    </Card>
  );
}



function InventorySettingsTab({ data, save, disabled }) {
  const [inv, setInv] = useState({
    stock_discount_trigger: "close_repair",
    allow_negative_admin: true,
    ...(data.inventory || {}),
  });
  const TRIGGERS = [
    { id: "close_repair", label: "Al cerrar reparación", desc: "Cuando el estado pase a 'listo para entregar' o 'entregado'." },
    { id: "delivery_note", label: "Al generar albarán", desc: "En el momento exacto en que se emite el albarán de entrega." },
    { id: "invoice", label: "Al generar factura", desc: "En el momento exacto en que se emite la factura." },
  ];
  return (
    <Card title="Inventario">
      <p className="text-xs text-[#525252] mb-4">Configura cuándo se descuenta el stock del inventario.</p>
      <div className="space-y-3 mb-4">
        {TRIGGERS.map((t) => (
          <label key={t.id} className={`flex items-start gap-3 p-3 border cursor-pointer ${inv.stock_discount_trigger === t.id ? "border-[#6E8B3D] bg-[#F7F7F4]" : "border-[#E5E5E0]"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
            <input
              type="radio"
              name="stock-trigger"
              checked={inv.stock_discount_trigger === t.id}
              onChange={() => setInv({ ...inv, stock_discount_trigger: t.id })}
              disabled={disabled}
              className="mt-0.5 accent-[#6E8B3D]"
              data-testid={`inventory-trigger-${t.id}`}
            />
            <div>
              <div className="font-bold text-sm">{t.label}</div>
              <div className="text-xs text-[#525252]">{t.desc}</div>
            </div>
          </label>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm mb-4">
        <input
          type="checkbox"
          checked={inv.allow_negative_admin !== false}
          onChange={(e) => setInv({ ...inv, allow_negative_admin: e.target.checked })}
          disabled={disabled}
          className="h-4 w-4 accent-[#6E8B3D]"
          data-testid="inventory-allow-negative"
        />
        Permitir que los administradores fuercen el descuento aunque no haya stock suficiente
      </label>
      <button
        type="button"
        onClick={() => save({ inventory: inv })}
        disabled={disabled}
        data-testid="inventory-save"
        className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
      >
        <Save size={14} /> Guardar
      </button>
    </Card>
  );
}


/**
 * Desplegable de garantía por defecto + gestor de opciones (añadir,
 * editar, desactivar, reordenar). El valor se guarda como entero de meses
 * (0 = "Sin garantía"). Las opciones se persisten en `billing.warranty_options`.
 * Reutilizado por Reparaciones, Productos, Inventario, etc.
 */
function WarrantyDefaultField({ b, setB, disabled }) {
  const [managing, setManaging] = useState(false);
  const opts = Array.isArray(b.warranty_options) && b.warranty_options.length
    ? b.warranty_options
    : [0, 3, 6, 12, 24];
  const current = Number.isFinite(Number(b.warranty_months_default))
    ? Number(b.warranty_months_default)
    : 3;
  const label = (m) => (m === 0 ? "Sin garantía" : m === 1 ? "1 mes" : `${m} meses`);

  const [draft, setDraft] = useState("");

  const updateOptions = (next) => {
    const sorted = Array.from(new Set(next.map(Number).filter((n) => Number.isFinite(n) && n >= 0)));
    // Mantenemos 0 al inicio si está presente; resto orden natural.
    sorted.sort((a, c) => a - c);
    setB({ ...b, warranty_options: sorted });
  };

  const addOption = () => {
    const n = parseInt(draft, 10);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Introduce un número entero de meses (0 = sin garantía)");
      return;
    }
    if (opts.includes(n)) {
      toast.info("Esa opción ya existe");
      return;
    }
    updateOptions([...opts, n]);
    setDraft("");
    toast.success(`Añadida: ${label(n)}`);
  };

  const removeOption = (n) => {
    if (!window.confirm(`¿Quitar la opción "${label(n)}"? No afectará a documentos históricos.`)) return;
    updateOptions(opts.filter((x) => x !== n));
    if (current === n) setB({ ...b, warranty_months_default: 3, warranty_options: opts.filter((x) => x !== n) });
  };

  return (
    <div className="md:col-span-2 border border-[#E5E5E0] p-3 bg-[#FAFAF7]">
      <div className="flex items-center justify-between mb-2">
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Garantía por defecto (meses)
        </label>
        <button
          type="button"
          onClick={() => setManaging(!managing)}
          className="text-[10px] font-mono uppercase tracking-wider text-[#6E8B3D] hover:underline"
          data-testid="warranty-options-toggle"
        >
          {managing ? "Cerrar" : "Gestionar garantías"}
        </button>
      </div>
      <select
        value={current}
        disabled={disabled}
        onChange={(e) => setB({ ...b, warranty_months_default: parseInt(e.target.value, 10) || 0 })}
        data-testid="billing-warranty-default"
        className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
      >
        {opts.map((m) => (
          <option key={m} value={m}>{label(m)}</option>
        ))}
      </select>
      {managing && (
        <div className="mt-3 border-t border-[#E5E5E0] pt-3 space-y-2">
          <div className="text-xs font-mono uppercase tracking-widest text-[#525252]">
            Opciones disponibles
          </div>
          <div className="flex flex-wrap gap-1.5">
            {opts.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-[#111110] text-xs"
                data-testid={`warranty-option-${m}`}
              >
                {label(m)}
                <button
                  type="button"
                  onClick={() => removeOption(m)}
                  className="hover:text-red-600"
                  title="Quitar de las opciones"
                  aria-label={`Quitar ${label(m)}`}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="1"
              placeholder="Nº meses (ej: 18)"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOption())}
              data-testid="warranty-option-add-input"
              className="flex-1 ds-card focus:border-[#6E8B3D] outline-none px-2 py-1 text-xs rounded-none"
            />
            <button
              type="button"
              onClick={addOption}
              data-testid="warranty-option-add-btn"
              className="bg-[#111110] hover:bg-[#6E8B3D] text-white px-2 py-1 text-xs inline-flex items-center gap-1"
            >
              <Plus size={11} /> Añadir
            </button>
          </div>
          <p className="text-[10px] text-[#525252]">
            Quitar una opción no afecta a documentos ya emitidos. Las garantías históricas siguen registradas tal cual.
          </p>
        </div>
      )}
    </div>
  );
}

